import { Loader } from "@tfg/ui"
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { OrganizationType } from "@tfg/types/types/organization.types"
import { UserRoles } from "@tfg/types/types/user.types"

import { httpClient } from "~/backend/axios"

type AuthContext = {
  isAuthenticated: boolean
  isLoading: boolean
  user: LibraryResponses.TFGUser | null
  isAdmin: boolean
  logout: () => void
  authenticate: (user: LibraryResponses.TFGUser) => void
  checkAuth: () => void
  homeRedirectUrl: string
  getHomeRedirectUrl: (user: LibraryResponses.TFGUser) => string
}

const DEFAULT_HOME_REDIRECT_URL = "/"
const SUPPLIER_HOME_REDIRECT_URL = "/supplier/requests/traceability"

export const AuthContext = createContext<AuthContext>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  logout: async () => void {},
  authenticate: () => void {},
  checkAuth: () => void {},
  homeRedirectUrl: DEFAULT_HOME_REDIRECT_URL,
  getHomeRedirectUrl: () => DEFAULT_HOME_REDIRECT_URL,
})

type Props = PropsWithChildren<object>

export function AuthContextProvider({ children }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [user, setUser] = useState<LibraryResponses.TFGUser | null>(null)
  const [homeRedirectUrl, setHomeRedirectUrl] = useState<string>(
    DEFAULT_HOME_REDIRECT_URL
  )
  const logout = useCallback(async () => {
    await httpClient.post("/auth/logout")
    setIsAuthenticated(false)
    setUser(null)
    setHomeRedirectUrl(DEFAULT_HOME_REDIRECT_URL)
    setIsLoading(false)
  }, [])

  const isAdmin = user?.role === UserRoles.SUPER_ADMIN
  const getHomeRedirectUrl = useCallback((user: LibraryResponses.TFGUser) => {
    if (!user?.organization) return DEFAULT_HOME_REDIRECT_URL
    return [OrganizationType.BRAND, OrganizationType.PLATFORM_ADMIN].includes(
      user.organization.type
    )
      ? DEFAULT_HOME_REDIRECT_URL
      : SUPPLIER_HOME_REDIRECT_URL
  }, [])

  const authenticate = useCallback(
    (user: LibraryResponses.TFGUser) => {
      setUser(user)
      setHomeRedirectUrl(getHomeRedirectUrl(user))
      setIsAuthenticated(true)
      setIsLoading(false)
    },
    [getHomeRedirectUrl]
  )

  const checkAuth = useCallback(async () => {
    setIsLoading(true)
    try {
      await httpClient.post("/auth/refresh-token").then((response) => {
        const user: LibraryResponses.TFGUser = response.data
        if (user && user?.organization) {
          setUser(user)
          setHomeRedirectUrl(getHomeRedirectUrl(user))
          setIsAuthenticated(true)
        } else {
          setIsAuthenticated(false)
        }
        setIsLoading(false)
      })
    } catch (_) {
      setIsAuthenticated(false)
      setIsLoading(false)
    }
  }, [getHomeRedirectUrl])

  useEffect(() => {
    ;(async () => {
      await checkAuth()
    })()
  }, [checkAuth])

  const value: AuthContext = {
    isAuthenticated,
    isLoading,
    user,
    isAdmin,
    logout,
    authenticate,
    checkAuth,
    homeRedirectUrl,
    getHomeRedirectUrl,
  }

  if (isLoading) {
    return <Loader fullPage />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within a AuthContextProvider")
  }
  return context
}
